import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { RootState } from './types';
import {
  findVariantByBarcode,
  getTotalVariantStock,
  isVariantProduct,
} from '../utils/variantHelpers';

export type ProductsObject = {
  docs: ProductObject[];
  hasNextPage: boolean;
  hasPrevPage: boolean;
  limit: number;
  nextPage: number;
  page: number;
  perPage: number;
  totalDocs: number;
  totalPages: number;
};
export type SearchedProductsObject = {
  data: ProductObject[];
  hasNextPage: boolean;
  hasPrevPage: boolean;
  limit: number;
  nextPage: number;
  page: number;
  perPage: number;
  totalDocs: number;
  totalPages: number;
};
export interface ProductVariant {
  _id: string;
  bp_variant_sku: string;
  bp_variant_attributes: Record<string, string>;
  bp_variant_barcode: string;
  bp_variant_buying_price: number;
  bp_variant_selling_price: number;
  bp_variant_quantity: number;
  bp_variant_image: string;
  bp_variant_minimum_stock_level: number;
  bp_variant_display_name: string;
}

export interface ProductAttribute {
  _id: string;
  bp_attribute_name: string;
  bp_attribute_values: string[];
  bp_attribute_location_id: string;
  bp_owner_id: string;
}

export interface ProductBarcodeMatch {
  product: ProductObject;
  matchedVariant: ProductVariant | null;
  matchedVariantId: string | null;
  matchedVariantIndex: number;
  matchedOn: 'product' | 'variant';
}
export interface ProductObject {
  bp_minimum_stock_level: number;
  bp_product_category: string;
  bp_product_mass: string;
  bp_product_model: string;
  bp_product_name: string;
  bp_product_quantity: number | null;
  bp_product_selling_price: number | null;
  bp_product_price_type: 'item' | 'unit';
  bp_product_image: string;
  bp_product_buying_price: number | null;
  bp_product_size: string;
  bp_product_sub_category: string;
  bp_product_unit_of_mass: string;
  _id: string;
  bp_product_barcode?: string;
  bp_product_is_favourite?: boolean;
  bp_product_has_variants?: boolean;
  bp_product_variant_attributes?: string[];
  bp_product_variants?: ProductVariant[];
}

export interface ProductsSliceObject {
  products: ProductsObject;
  categories: string[];
  subcategories: [];
  searchedproducts: SearchedProductsObject;
  productsbysubcategory: ProductsObject;
  productsnames: [];
  searchedproductsfromscanner: SearchedProductsObject;
}

const initialProducts = {
  docs: [],
  hasNextPage: true,
  hasPrevPage: true,
  limit: 100,
  nextPage: 1,
  page: 0,
  perPage: 100,
  totalDocs: 0,
  totalPages: 0,
};
const initialState: ProductsSliceObject = {
  products: initialProducts,
  categories: [],
  subcategories: [],
  searchedproducts: {
    data: [],
    hasNextPage: true,
    hasPrevPage: true,
    limit: 100,
    nextPage: 1,
    page: 0,
    perPage: 100,
    totalDocs: 0,
    totalPages: 0,
  },
  productsbysubcategory: initialProducts,
  productsnames: [],
  searchedproductsfromscanner: {
    data: [],
    hasNextPage: true,
    hasPrevPage: true,
    limit: 100,
    nextPage: 1,
    page: 0,
    perPage: 100,
    totalDocs: 0,
    totalPages: 0,
  },
};

const hasVariantQuantityChanged = (
  currentProduct: ProductObject,
  incomingProduct: ProductObject,
) => {
  const currentVariants = currentProduct.bp_product_variants ?? [];
  const incomingVariants = incomingProduct.bp_product_variants ?? [];
  if (currentVariants.length !== incomingVariants.length) return true;

  const currentQuantityMap = new Map(
    currentVariants.map(variant => [variant._id, variant.bp_variant_quantity]),
  );

  for (const variant of incomingVariants) {
    if (!currentQuantityMap.has(variant._id)) return true;
    if (currentQuantityMap.get(variant._id) !== variant.bp_variant_quantity) {
      return true;
    }
  }

  return false;
};

const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    addupdateProducts: (state, action: PayloadAction<ProductsObject>) => {
      if (!action.payload || !action.payload.docs) {
        console.error('Invalid payload in updateProducts');
        return;
      }

      const payloadMap = new Map(action.payload.docs.map(p => [p._id, p]));

      state.products.docs = state.products.docs.map(product => {
        const payloadProduct = payloadMap.get(product._id);
        if (payloadProduct) {
          // Always update with latest data from backend (source of truth)
          // This ensures all fields (image, price, quantity, etc.) are updated
          return { ...payloadProduct };
        }
        return product;
      });

      // Add new products that don't exist in the current state
      action.payload.docs.forEach(payloadProduct => {
        if (!state.products.docs.some(p => p._id === payloadProduct._id)) {
          state.products.docs.push(payloadProduct);
        }
      });

      // Update other ProductsObject properties
      state.products = {
        ...state.products,
        hasNextPage: action.payload.hasNextPage,
        hasPrevPage: action.payload.hasPrevPage,
        limit: action.payload.limit,
        nextPage: action.payload.nextPage,
        page: action.payload.page,
        perPage: action.payload.perPage,
        totalDocs: action.payload.totalDocs,
        totalPages: action.payload.totalPages,
      };
    },
    updateProductsAfterCheckout: (
      state,
      action: PayloadAction<ProductObject[]>,
    ) => {
      const payloadMap = new Map(action.payload.map(p => [p._id, p]));
      //update the products that have changed
      state.products.docs = state.products.docs.map(product => {
        const payloadProduct = payloadMap.get(product._id);
        if (!payloadProduct) return product;

        const variantQuantityChanged =
          (isVariantProduct(product) || isVariantProduct(payloadProduct)) &&
          hasVariantQuantityChanged(product, payloadProduct);

        const productQuantityChanged =
          (payloadProduct.bp_product_quantity ?? 0) !==
          (product.bp_product_quantity ?? 0);

        if (variantQuantityChanged || productQuantityChanged) {
          return { ...product, ...payloadProduct };
        }
        return product;
      });

      //add new products that don't exist in the current state if they exist in the payload
      action.payload.forEach(payloadProduct => {
        if (!state.products.docs.some(p => p._id === payloadProduct._id)) {
          state.products.docs.push(payloadProduct);
        }
      });
    },
    addProductsBySubCategory: (
      state,
      action: PayloadAction<ProductsObject>,
    ) => {
      state.productsbysubcategory = action.payload;
    },
    updateProductsBySubCategory: (
      state,
      action: PayloadAction<ProductsObject>,
    ) => {
      // Build a map of incoming products for quick lookup
      const payloadMap = new Map(action.payload.docs.map(p => [p._id, p]));

      // Update existing docs in-place with latest backend info while preserving order
      const updatedDocs = state.productsbysubcategory.docs.map(existing => {
        const incoming = payloadMap.get(existing._id);
        if (incoming) {
          return { ...existing, ...incoming };
        }
        return existing;
      });

      // Append any new products that do not exist locally
      const existingIds = new Set(updatedDocs.map(p => p._id));
      for (const incoming of action.payload.docs) {
        if (!existingIds.has(incoming._id)) {
          updatedDocs.push(incoming);
        }
      }

      state.productsbysubcategory = {
        ...state.productsbysubcategory,
        docs: updatedDocs,
        hasNextPage: action.payload.hasNextPage,
        hasPrevPage: action.payload.hasPrevPage,
        limit: action.payload.limit,
        nextPage: action.payload.nextPage,
        page: action.payload.page,
        perPage: action.payload.perPage,
        totalDocs: action.payload.totalDocs,
        totalPages: action.payload.totalPages,
      };

      // Also merge incoming subcategory updates into the global products.docs
      if (action.payload.docs && action.payload.docs.length > 0) {
        const globalMap = new Map(state.products.docs.map(p => [p._id, p]));
        // Update existing
        state.products.docs = state.products.docs.map(existing => {
          const incoming = payloadMap.get(existing._id);
          return incoming ? { ...existing, ...incoming } : existing;
        });
        // Append new
        for (const incoming of action.payload.docs) {
          if (!globalMap.has(incoming._id)) {
            state.products.docs.push(incoming);
          }
        }
      }
    },
    seedProductsBySubCategoryFromLocal: (
      state,
      action: PayloadAction<string>,
    ) => {
      const subcategory = action.payload;
      const key = String(subcategory || '').trim();
      const docs = state.products.docs;
      const filtered = docs.filter(
        p =>
          String(p.bp_product_sub_category || '').trim() === key ||
          String(p.bp_product_category || '').trim() === key,
      );

      state.productsbysubcategory = {
        ...state.productsbysubcategory,
        docs: filtered,
        // Seed minimal pagination; real values will be updated by backend response
        page: 1,
        nextPage: 1,
        perPage: state.products.perPage,
        limit: state.products.limit,
        totalDocs: filtered.length,
        totalPages: state.productsbysubcategory.totalPages,
        // Avoid triggering load-more until backend confirms
        hasNextPage: false,
        hasPrevPage: false,
      };
    },
    /** Same outcome as seedProductsBySubCategoryFromLocal but O(1) lookup via selectProductsByTabKeyMap. */
    seedProductsBySubCategoryFromDocs: (
      state,
      action: PayloadAction<ProductObject[]>,
    ) => {
      const filtered = action.payload;
      state.productsbysubcategory = {
        ...state.productsbysubcategory,
        docs: filtered,
        page: 1,
        nextPage: 1,
        perPage: state.products.perPage,
        limit: state.products.limit,
        totalDocs: filtered.length,
        totalPages: state.productsbysubcategory.totalPages,
        hasNextPage: false,
        hasPrevPage: false,
      };
    },
    seedSearchProductsFromLocal: (state, action: PayloadAction<string>) => {
      const term = (action.payload || '').trim().toLowerCase();
      const filtered = term.length
        ? state.products.docs.filter(p =>
            String(p.bp_product_name || '')
              .toLowerCase()
              .includes(term),
          )
        : [];

      state.searchedproducts = {
        ...state.searchedproducts,
        data: filtered,
        page: 1,
        nextPage: 1,
        perPage: state.products.perPage,
        limit: state.products.limit,
        totalDocs: filtered.length,
        totalPages: 1,
        hasNextPage: false,
        hasPrevPage: false,
      };
    },
    addSearchProducts: (
      state,
      action: PayloadAction<SearchedProductsObject>,
    ) => {
      state.searchedproducts = action.payload;
    },
    updateSearchProducts: (
      state,
      action: PayloadAction<SearchedProductsObject>,
    ) => {
      const incoming = action.payload;
      const incomingMap = new Map(incoming.data.map(p => [p._id, p]));

      // Update existing items in-place to latest from backend while preserving order
      const updatedExisting = state.searchedproducts.data.map(existing => {
        const newer = incomingMap.get(existing._id);
        return newer ? { ...existing, ...newer } : existing;
      });

      // Append only truly new items (not present locally)
      const existingIds = new Set(updatedExisting.map(p => p._id));
      const appended = incoming.data.filter(p => !existingIds.has(p._id));

      state.searchedproducts = {
        ...state.searchedproducts,
        data: [...updatedExisting, ...appended],
        hasNextPage: incoming.hasNextPage,
        hasPrevPage: incoming.hasPrevPage,
        limit: incoming.limit,
        nextPage: incoming.nextPage,
        page: incoming.page,
        perPage: incoming.perPage,
        totalDocs: Math.max(
          incoming.totalDocs,
          updatedExisting.length + appended.length,
        ),
        totalPages: incoming.totalPages,
      };
    },
    resetSearchProducts: state => {
      state.searchedproducts = initialState.searchedproducts;
    },
    addCategories: (state, action: PayloadAction<ProductObject[]>) => {
      action.payload.forEach(product => {
        if (!state.categories.includes(product?.bp_product_category)) {
          state.categories.push(product?.bp_product_category);
        }
      });
    },
    addSubCategories: (state, action) => {
      state.subcategories = action.payload;
    },

    clearAllProducts: state => {
      state.products = initialState.products;
    },
    prioritizeProductsByProductObject: (
      state,
      action: PayloadAction<ProductObject[]>,
    ) => {
      const priorityProducts = action.payload;
      const priorityMap = new Map(
        priorityProducts.map(item => [item._id, item]),
      );
      const priorityIds = new Set(priorityMap.keys());

      // Update existing products and add new ones
      const updatedDocs = state.products.docs.map(product =>
        priorityMap.has(product._id)
          ? { ...product, ...priorityMap.get(product._id)! }
          : product,
      );

      // Add new products that aren't in docs
      const existingIds = new Set(state.products.docs.map(p => p._id));
      for (const newProduct of priorityProducts) {
        if (!existingIds.has(newProduct._id)) {
          updatedDocs.push(newProduct);
        }
      }

      // Sort the array to prioritize the updated/new products
      updatedDocs.sort((a, b) => {
        const aIsPriority = priorityIds.has(a._id);
        const bIsPriority = priorityIds.has(b._id);
        if (aIsPriority && !bIsPriority) return -1;
        if (!aIsPriority && bIsPriority) return 1;
        return 0;
      });

      // Update the state
      state.products.docs = updatedDocs;
    },
    addProductsNames: (state, action) => {
      state.productsnames = action.payload;
    },
    addSearchProductsFromScanner: (
      state,
      action: PayloadAction<SearchedProductsObject>,
    ) => {
      const existingIds = new Set(
        state.searchedproductsfromscanner.data.map(p => p._id),
      );
      const newProducts = action.payload.data.filter(
        product => !existingIds.has(product._id),
      );
      state.searchedproductsfromscanner = {
        ...action.payload,
        data: [...state.searchedproductsfromscanner.data, ...newProducts],
      };
    },
    clearSearchedProductsFromScanner: state => {
      state.searchedproductsfromscanner =
        initialState.searchedproductsfromscanner;
    },
    clearCategories: state => {
      state.categories = [];
    },
    clearSubCategories: state => {
      state.subcategories = [];
    },
    clearProductsNames: state => {
      state.productsnames = [];
    },
    clearProductsBySubCategory: state => {
      state.productsbysubcategory = initialState.productsbysubcategory;
    },
    setProductImage: (
      state,
      action: PayloadAction<{ productId: string; imageUrl: string }>,
    ) => {
      const { productId, imageUrl } = action.payload;
      // Update in main products list
      state.products.docs = state.products.docs.map(p =>
        p._id === productId ? ({ ...p, bp_product_image: imageUrl } as any) : p,
      );
      // Update in subcategory list
      state.productsbysubcategory.docs = state.productsbysubcategory.docs.map(
        p =>
          p._id === productId
            ? ({ ...p, bp_product_image: imageUrl } as any)
            : p,
      );
      // Update in searched lists if present
      state.searchedproducts.data = state.searchedproducts.data.map(p =>
        p._id === productId ? ({ ...p, bp_product_image: imageUrl } as any) : p,
      );
      state.searchedproductsfromscanner.data =
        state.searchedproductsfromscanner.data.map(p =>
          p._id === productId
            ? ({ ...p, bp_product_image: imageUrl } as any)
            : p,
        );
    },
    mergeProductFields: (
      state,
      action: PayloadAction<{
        productId: string;
        updates: Record<string, any>;
      }>,
    ) => {
      const { productId, updates } = action.payload;
      state.products.docs = state.products.docs.map(p =>
        p._id === productId ? ({ ...p, ...updates } as any) : p,
      );
      state.productsbysubcategory.docs = state.productsbysubcategory.docs.map(
        p => (p._id === productId ? ({ ...p, ...updates } as any) : p),
      );
      state.searchedproducts.data = state.searchedproducts.data.map(p =>
        p._id === productId ? ({ ...p, ...updates } as any) : p,
      );
      state.searchedproductsfromscanner.data =
        state.searchedproductsfromscanner.data.map(p =>
          p._id === productId ? ({ ...p, ...updates } as any) : p,
        );
    },
    mergeProductDocs: (state, action: PayloadAction<ProductObject[]>) => {
      const incomingProducts = action.payload.filter(product => product?._id);
      if (!incomingProducts.length) return;

      const incomingMap = new Map(
        incomingProducts.map(product => [product._id, product]),
      );

      state.products.docs = state.products.docs.map(product => {
        const incomingProduct = incomingMap.get(product._id);
        return incomingProduct ? { ...product, ...incomingProduct } : product;
      });

      const existingIds = new Set(state.products.docs.map(p => p._id));
      incomingProducts.forEach(product => {
        if (!existingIds.has(product._id)) {
          state.products.docs.push(product);
          existingIds.add(product._id);
        }
      });

      state.productsbysubcategory.docs = state.productsbysubcategory.docs.map(
        product => {
          const incomingProduct = incomingMap.get(product._id);
          return incomingProduct
            ? { ...product, ...incomingProduct }
            : product;
        },
      );

      state.searchedproducts.data = state.searchedproducts.data.map(product => {
        const incomingProduct = incomingMap.get(product._id);
        return incomingProduct ? { ...product, ...incomingProduct } : product;
      });

      state.searchedproductsfromscanner.data =
        state.searchedproductsfromscanner.data.map(product => {
          const incomingProduct = incomingMap.get(product._id);
          return incomingProduct
            ? { ...product, ...incomingProduct }
            : product;
        });
    },
    removeProductById: (state, action: PayloadAction<string>) => {
      const productId = action.payload;
      if (!productId) return;

      state.products.docs = state.products.docs.filter(
        product => product._id !== productId,
      );

      state.productsbysubcategory.docs =
        state.productsbysubcategory.docs.filter(
          product => product._id !== productId,
        );

      state.searchedproducts.data = state.searchedproducts.data.filter(
        product => product._id !== productId,
      );

      state.searchedproductsfromscanner.data =
        state.searchedproductsfromscanner.data.filter(
          product => product._id !== productId,
        );
    },
  },
});

export const {
  // addProducts,
  addCategories,
  addSubCategories,
  clearAllProducts,
  addSearchProducts,
  addupdateProducts,
  addProductsBySubCategory,
  updateSearchProducts,
  updateProductsBySubCategory,
  seedProductsBySubCategoryFromLocal,
  seedProductsBySubCategoryFromDocs,
  resetSearchProducts,
  updateProductsAfterCheckout,
  addProductsNames,
  addSearchProductsFromScanner,
  clearSearchedProductsFromScanner,
  prioritizeProductsByProductObject,
  clearCategories,
  clearSubCategories,
  clearProductsNames,
  clearProductsBySubCategory,
  setProductImage,
  mergeProductFields,
  mergeProductDocs,
  removeProductById,
  seedSearchProductsFromLocal,
} = productsSlice.actions;
export default productsSlice.reducer;

// Basic memoized selectors
export const selectProducts = (state: RootState) => state.products.products;

export const selectSearchedProducts = (state: RootState) =>
  state.products.searchedproducts;

export const selectProductsBySubCategory = (state: RootState) =>
  state.products.productsbysubcategory;

export const selectCategories = (state: RootState) => state.products.categories;

export const selectSubCategories = (state: RootState) =>
  state.products.subcategories;

export const selectProductsNames = (state: RootState) =>
  state.products.productsnames;

export const selectSearchedProductsFromScanner = (state: RootState) =>
  state.products.searchedproductsfromscanner;

// Memoized product docs
export const selectProductDocs = createSelector(
  [selectProducts],
  products => products.docs,
);

// Memoized searched product docs
export const selectSearchedProductDocs = createSelector(
  [selectSearchedProducts],
  searchedProducts => searchedProducts.data,
);

// Memoized products by subcategory docs
export const selectProductsBySubCategoryDocs = createSelector(
  [selectProductsBySubCategory],
  productsBySubCategory => productsBySubCategory.docs,
);

// Memoized products from scanner docs
export const selectSearchedProductsFromScannerDocs = createSelector(
  [selectSearchedProductsFromScanner],
  scannerProducts => scannerProducts.data,
);

// Memoized products filtered by category
export const selectProductsByCategory = createSelector(
  [selectProductDocs, (state: RootState, category: string) => category],
  (products, category) => {
    if (!category) return products;
    return products.filter(product => product.bp_product_category === category);
  },
);

// Memoized products filtered by subcategory
export const selectProductsBySubCategoryFilter = createSelector(
  [selectProductDocs, (state: RootState, subcategory: string) => subcategory],
  (products, subcategory) => {
    if (!subcategory) return products;
    return products.filter(
      product => product.bp_product_sub_category === subcategory,
    );
  },
);

// Memoized products with stock (quantity > 0)
export const selectProductsWithStock = createSelector(
  [selectProductDocs],
  products =>
    products.filter(product => {
      if (isVariantProduct(product)) return getTotalVariantStock(product) > 0;
      return (product.bp_product_quantity ?? 0) > 0;
    }),
);

// Memoized products out of stock
export const selectProductsOutOfStock = createSelector(
  [selectProductDocs],
  products =>
    products.filter(product => {
      if (isVariantProduct(product)) return getTotalVariantStock(product) <= 0;
      return (product.bp_product_quantity ?? 0) <= 0;
    }),
);

// Memoized product categories (unique)
export const selectUniqueCategories = createSelector(
  [selectProductDocs],
  products => {
    const categories = new Set(
      products.map(product => product.bp_product_category),
    );
    return Array.from(categories).filter(Boolean);
  },
);

// Memoized product subcategories (unique)
export const selectUniqueSubCategories = createSelector(
  [selectProductDocs],
  products => {
    const subcategories = new Set(
      products.map(product => product.bp_product_sub_category),
    );
    return Array.from(subcategories).filter(Boolean);
  },
);

// Memoized products search by name
export const selectProductsByName = createSelector(
  [selectProductDocs, (state: RootState, searchTerm: string) => searchTerm],
  (products, searchTerm) => {
    if (!searchTerm) return products;
    const lowerSearchTerm = searchTerm.toLowerCase();
    return products.filter(product =>
      product.bp_product_name.toLowerCase().includes(lowerSearchTerm),
    );
  },
);

// Memoized product lookup by exact barcode match
export const selectProductsByBarcode = createSelector(
  [selectProductDocs, (state: RootState, barcode: string) => barcode],
  (products, barcode) => {
    if (!barcode) return [] as ProductBarcodeMatch[];
    const trimmed = barcode.trim();
    if (!trimmed) return [] as ProductBarcodeMatch[];

    return products.reduce<ProductBarcodeMatch[]>((matches, product) => {
      if (
        product.bp_product_barcode &&
        String(product.bp_product_barcode).trim() === trimmed
      ) {
        matches.push({
          product,
          matchedVariant: null,
          matchedVariantId: null,
          matchedVariantIndex: -1,
          matchedOn: 'product',
        });
        return matches;
      }

      const matchedVariant = findVariantByBarcode(product, trimmed);
      if (!matchedVariant) return matches;

      const matchedVariantIndex = (product.bp_product_variants ?? []).findIndex(
        variant => variant._id === matchedVariant._id,
      );
      matches.push({
        product,
        matchedVariant,
        matchedVariantId: matchedVariant._id,
        matchedVariantIndex,
        matchedOn: 'variant',
      });
      return matches;
    }, []);
  },
);

// Helper: builds a Map keyed by category/subcategory for O(1) lookup on tab switch.
// Each product appears in buckets for both its category and subcategory (if set).
function buildProductsByTabKey(
  docs: ProductObject[],
): Map<string, ProductObject[]> {
  const map = new Map<string, ProductObject[]>();
  for (const p of docs) {
    const keys = new Set<string>();
    const cat = String(p.bp_product_category ?? '').trim();
    const sub = String(p.bp_product_sub_category ?? '').trim();
    if (cat) keys.add(cat);
    if (sub) keys.add(sub);
    for (const key of keys) {
      let bucket = map.get(key);
      if (!bucket) {
        bucket = [];
        map.set(key, bucket);
      }
      bucket.push(p);
    }
  }
  return map;
}

// Memoized map of products indexed by category/subcategory key.
// Recomputes only when products.docs reference changes.
export const selectProductsByTabKeyMap = createSelector(
  [selectProductDocs],
  buildProductsByTabKey,
);
